import { createTrackingUtils } from "../index";
import useProfiler from "../../../hooks/useProfiler";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { UIFlightData } from "@flights/types";
import useIsInViewport from "../../../hooks/useIsInViewport";

const util = createTrackingUtils({
  name: "flights_web_aa_sr_badges_combination_desktop",
  defaultStage: 1,
  stages: {
    main: 0,
    all: 2,
    any3: 3,
    onlyBestCheapest: 4,
    onlyBestFastest: 5,
    onlyBestCheapestDirect: 6,
    onlyCheapestFastest: 7,
    onlyCheapestCheapestDirect: 8,
    onlyFastestCheapestDirect: 9
  },
  goals: {
    all: 1,
    any3: 2,
    includeCheapest: 3,
    includeFastest: 4,
    includeCheapestDirect: 5
  },
  goalsWithValue: []
});

export default function useFlightsWebAaSrBadgesCombinationDesktop(
  ref: RefObject<HTMLDivElement>,
  flight: UIFlightData
) {
  const [isTracked, setIsTracked] = useState(false);
  const isInViewport = useIsInViewport(ref);
  const { isDesktop, isMeta } = useProfiler();

  const canTrack = useMemo(() => isDesktop() && !isMeta(), [isDesktop, isMeta]);

  const badges = useMemo(() => {
    const list = ["BEST", "CHEAPEST", "FASTEST", "CHEAPEST_DIRECT"];
    return flight.badges?.map((b) => b.type).filter((b) => list.includes(b)) || [];
  }, [flight]);

  const trackStages = useCallback(() => {
    if (badges.length === 4) util.stages.all();
    if (badges.length === 3) util.stages.any3();
    if (badges.length === 2) {
      if (badges.includes("BEST") && badges.includes("CHEAPEST")) util.stages.onlyBestCheapest();
      if (badges.includes("BEST") && badges.includes("FASTEST")) util.stages.onlyBestFastest();
      if (badges.includes("BEST") && badges.includes("CHEAPEST_DIRECT")) util.stages.onlyBestCheapestDirect();
      if (badges.includes("CHEAPEST") && badges.includes("FASTEST")) util.stages.onlyCheapestFastest();
      if (badges.includes("CHEAPEST") && badges.includes("CHEAPEST_DIRECT")) util.stages.onlyCheapestCheapestDirect();
      if (badges.includes("FASTEST") && badges.includes("CHEAPEST_DIRECT")) util.stages.onlyFastestCheapestDirect();
    }
  }, [badges]);

  const trackOnClickCombination = useCallback(() => {
    if (!canTrack) return;
    if (badges.length === 4) util.goals.all();
    if (badges.length === 3) util.goals.any3();
    if (badges.includes("CHEAPEST")) util.goals.includeCheapest();
    if (badges.includes("FASTEST")) util.goals.includeFastest();
    if (badges.includes("CHEAPEST_DIRECT")) util.goals.includeCheapestDirect();
  }, [canTrack, badges]);

  useEffect(() => {
    if (!isInViewport || isTracked || !canTrack) return;
    trackStages();
    setIsTracked(true);
  }, [isInViewport, isTracked, trackStages, canTrack]);

  return { trackOnClickCombination };
}
